// components/qualificacao/QualificarNoChat.js — qualificar o lead pelo topo
// do chat (out/26, rastreamento PR 2).
//
// É ali que o atendente decide se o lead presta: sem marcação, o botão diz
// "Qualificar"; marcado, vira a pílula da marcação (clique pra mudar). O
// painel é o mesmo da janela do contato. Depois de marcar, "Desfazer" fica
// 10 s ao lado.
//
// Redesign de out/26 ("Chat Cetto.dc.html"): pílula de 36 px no computador,
// do tamanho dos outros botões do topo; no celular, círculo de 40 px só com
// o ícone — o topo divide 390 px com o nome, o número, o vídeo e o contato.
// Marcado, o ícone diz QUAL marcação é (não só a cor): ✓ qualificado, ✕
// desqualificado, $ venda.
//
// Quem usa passa `key={cliente.id}`: o "Desfazer" é daquele contato, e sem
// a chave ele ficava ao lado da conversa seguinte (desfazendo a marcação do
// contato anterior).

import { useState } from 'react';
import { Box, Tooltip } from '@mui/material';
import { FiAward, FiCheckCircle, FiXCircle, FiDollarSign } from 'react-icons/fi';
import PainelQualificar from './PainelQualificar';
import useQualificar from './useQualificar';
import { STATUS } from './regras';

const FONTE = "'Outfit', system-ui, sans-serif";
const ICONE = { qualificado: FiCheckCircle, desqualificado: FiXCircle, venda: FiDollarSign };

export default function QualificarNoChat({ cliente, onMudou }) {
  const [anchor, setAnchor] = useState(null);
  const status = cliente?.lead_status || null;
  const t = STATUS[status];
  const { marcar, desfazer, erroDesfazer, desfazerAgora } = useQualificar(onMudou);

  if (!cliente?.id) return null;
  return (
    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: '6px' }}>
      <Tooltip title={t ? `${t.rotulo} — clique para mudar` : 'Qualificar o lead'}>
        <Box component="button" type="button" onClick={(e) => setAnchor(e.currentTarget)}
          aria-label={t ? `Lead: ${t.rotulo}. Mudar a marcação` : 'Qualificar o lead'}
          aria-haspopup="dialog"
          sx={{
            height: { xs: 40, md: 36 }, minWidth: { xs: 40, md: 36 }, p: { xs: 0, md: '0 14px' },
            borderRadius: 999, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '7px',
            fontFamily: FONTE, fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap',
            border: `1px solid ${t ? t.borda : 'transparent'}`, bgcolor: t ? t.bg : 'var(--ch-inset)',
            color: t ? t.cor : 'var(--ch-text2)',
            transition: 'border-color .15s, color .15s',
            '&:hover': { borderColor: t ? t.ponto : 'var(--ch-bdr2)', color: t ? t.cor : 'var(--ch-text)' },
            '&:focus-visible': { outline: '2px solid var(--ch-green)', outlineOffset: 2 },
          }}>
          {/* Celular: só o ícone (o da marcação, ou o de qualificar). */}
          <Box component="span" aria-hidden sx={{ display: { xs: 'flex', md: 'none' } }}>
            {(() => { const I = (status && ICONE[status]) || FiAward; return <I size={17} />; })()}
          </Box>
          {/* Computador: ponto + rótulo, ou ícone + "Qualificar". */}
          <Box component="span" sx={{ display: { xs: 'none', md: 'inline-flex' }, alignItems: 'center', gap: '7px' }}>
            {t
              ? <Box component="span" aria-hidden sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: t.ponto, flexShrink: 0 }} />
              : <FiAward size={15} aria-hidden />}
            {t ? t.curto : 'Qualificar'}
          </Box>
        </Box>
      </Tooltip>
      {/* O aviso é lido pelo leitor de tela (região viva fora do botão —
          `role` no próprio botão tirava dele o papel de botão). Tamanho em
          TEXTO, '1px': no `sx` do MUI o número 1 vira 100%, e essa caixa
          invisível da largura da tela, presa no canto do topo do chat, abria
          a rolagem lateral com a direita em branco (out/26, depois de
          qualificar). O `position: relative` do pai a mantém aqui dentro. */}
      <Box component="span" role={erroDesfazer ? 'alert' : 'status'} sx={{
        position: 'absolute', left: 0, top: 0, width: '1px', height: '1px', m: '-1px', p: 0, border: 0,
        overflow: 'hidden', clip: 'rect(0 0 0 0)', clipPath: 'inset(50%)', whiteSpace: 'nowrap',
      }}>
        {erroDesfazer || desfazer?.texto || ''}
      </Box>
      {(desfazer || erroDesfazer) && (
        <Box component="button" type="button" onClick={desfazerAgora} disabled={Boolean(erroDesfazer)}
          title={erroDesfazer || desfazer?.texto}
          sx={{
            height: 36, px: '8px', border: 0, borderRadius: 999, bgcolor: 'transparent', cursor: 'pointer',
            fontFamily: FONTE, fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap',
            color: erroDesfazer ? 'var(--ch-red-d)' : 'var(--ch-text2)', textDecoration: erroDesfazer ? 'none' : 'underline',
            textUnderlineOffset: 3, '&:hover': { color: 'var(--ch-text)' },
            '&:focus-visible': { outline: '2px solid var(--ch-green)', outlineOffset: 2 },
            '&:disabled': { cursor: 'default' },
          }}>
          {erroDesfazer ? 'Não deu para desfazer' : 'Desfazer'}
        </Box>
      )}
      <PainelQualificar
        anchorEl={anchor}
        atual={status}
        onClose={() => setAnchor(null)}
        onEscolher={(pedido) => marcar(cliente.id, pedido)}
      />
    </Box>
  );
}
